import { Navigate, Route, Routes } from 'react-router-dom';
import { RoleShell } from '../layouts/RoleShell';
import { HomePage } from '../pages/shared/HomePage';
import { PlaceholderPage } from '../pages/shared/PlaceholderPage';
import { StudentDashboard } from '../pages/student/StudentDashboard';
import { TodayMathPage } from '../pages/student/TodayMathPage';
import { StudentProgressPage } from '../pages/student/StudentProgressPage';
import { TeacherStudentMonitorPage } from '../pages/teacher/TeacherStudentMonitorPage';
import { TeacherDashboard } from '../pages/teacher/TeacherDashboard';
import { ParentDashboard } from '../pages/parent/ParentDashboard';
import { ParentProgressPage } from '../pages/parent/ParentProgressPage';
import { AdminDashboard } from '../pages/admin/AdminDashboard';
import { AdminStudentsPage } from '../pages/admin/AdminStudentsPage';
import { AdminDevSetupPage } from '../pages/admin/AdminDevSetupPage';
import { PublicResourceResolverPage } from '../pages/resource/PublicResourceResolverPage';
import { PrivateAssignmentResolverPage } from '../pages/assignment/PrivateAssignmentResolverPage';
import { PrintableAssignmentPage } from '../pages/assignment/PrintableAssignmentPage';
import { LoginPage } from '../pages/auth/LoginPage';

const studentNav = [
  { to: '/student', label: 'Beranda' },
  { to: '/student/today', label: 'Belajar Hari Ini' },
  { to: '/student/homework', label: 'PR Saya' },
  { to: '/student/listening', label: 'Listening' },
  { to: '/student/progress', label: 'Progress' },
  { to: '/student/help', label: 'Bantuan' },
];

const teacherNav = [
  { to: '/teacher', label: 'Dashboard' },
  { to: '/teacher/students', label: 'Murid' },
  { to: '/teacher/assignments', label: 'PR' },
  { to: '/teacher/scan', label: 'Scan' },
  { to: '/teacher/progress', label: 'Progress' },
];

const parentNav = [
  { to: '/parent', label: 'Beranda' },
  { to: '/parent/children', label: 'Anak Saya' },
  { to: '/parent/homework', label: 'PR' },
  { to: '/parent/progress', label: 'Progress' },
  { to: '/parent/reports', label: 'Laporan' },
];

const adminNav = [
  { to: '/admin', label: 'Dashboard' },
  { to: '/admin/students', label: 'Murid' },
  { to: '/admin/curriculum', label: 'Kurikulum' },
  { to: '/admin/worksheets', label: 'Worksheet' },
  { to: '/admin/assets', label: 'Aset' },
  { to: '/admin/audit', label: 'Audit' },
  { to: '/admin/dev-setup', label: 'DEV Setup' },
];

function P({ title, description, next }: { title: string; description: string; next?: string }) {
  return <PlaceholderPage title={title} description={description} next={next} />;
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/login" element={<LoginPage />} />

      <Route path="/r/:shortCode" element={<PublicResourceResolverPage />} />
      <Route path="/a/:token" element={<PrivateAssignmentResolverPage />} />
      <Route path="/print/assignment/:assignmentId" element={<PrintableAssignmentPage />} />

      <Route path="/student" element={<RoleShell roleLabel="Murid" nav={studentNav} allowedRoles={['student']} />}>
        <Route index element={<StudentDashboard />} />
        <Route path="today" element={<TodayMathPage />} />
        <Route path="homework" element={<P title="PR Saya" description="Assignment siswa akan tampil di sini." next="TECH-08 Assignment Engine" />} />
        <Route path="listening" element={<P title="Listening" description="Audio dan replay tracking akan terhubung di sini." next="TECH-12 Audio + Listening" />} />
        <Route path="progress" element={<StudentProgressPage />} />
        <Route path="help" element={<P title="Bantuan" description="Guided UI dan petunjuk anak." next="TECH-14 Guidance" />} />
      </Route>

      <Route path="/teacher" element={<RoleShell roleLabel="Guru" nav={teacherNav} allowedRoles={['teacher', 'branch_admin', 'superadmin']} />}>
        <Route index element={<TeacherDashboard />} />
        <Route path="students" element={<TeacherStudentMonitorPage />} />
        <Route path="assignments" element={<P title="Assignment / PR" description="Generate, preview, dan batch PR." next="TECH-08 Assignment Engine" />} />
        <Route path="scan" element={<P title="Scan QR" description="Lookup QR dan correction workflow." next="TECH-08 QR + Assignment" />} />
        <Route path="progress" element={<P title="Progress" description="Analitik mastery dan recommendation." next="TECH-11 Mastery + Recommendation" />} />
      </Route>

      <Route path="/parent" element={<RoleShell roleLabel="Orang Tua" nav={parentNav} allowedRoles={['guardian']} />}>
        <Route index element={<ParentDashboard />} />
        <Route path="children" element={<P title="Anak Saya" description="Hubungan guardian dengan satu atau lebih siswa." next="TECH-04 Student Management" />} />
        <Route path="homework" element={<P title="PR" description="Status PR anak tanpa hak mengubah assignment." next="TECH-13 Parent Dashboard" />} />
        <Route path="progress" element={<ParentProgressPage />} />
        <Route path="reports" element={<P title="Laporan" description="Laporan berkala dan print/PDF." next="TECH-13 Parent Dashboard" />} />
      </Route>

      <Route path="/admin" element={<RoleShell roleLabel="Admin" nav={adminNav} allowedRoles={['branch_admin', 'superadmin']} />}>
        <Route index element={<AdminDashboard />} />
        <Route path="students" element={<AdminStudentsPage />} />
        <Route path="curriculum" element={<P title="Kurikulum" description="Subject, level, skill, unit, prerequisite." next="TECH-05 Curriculum Engine" />} />
        <Route path="worksheets" element={<P title="Worksheet" description="Content versioning dan publishing workflow." next="TECH-06 Content Engine" />} />
        <Route path="assets" element={<P title="Aset" description="Illustration, audio, dan asset registry." next="TECH-06 Content Engine" />} />
        <Route path="audit" element={<P title="Audit" description="Log perubahan sensitif." next="TECH-15 Audit/Security" />} />
        <Route path="dev-setup" element={<AdminDevSetupPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
